import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { SaveBar } from "../../utils/SaveBar";
import {
  fetchHseDigestSettings,
  updateHseDigestSettings,
} from "../../../api/hse/digestSettings";
import {
  HSE_DIGEST_TIMEZONE,
  HSE_DIGEST_WEEKDAY_KEYS,
  HSE_DIGEST_WEEKDAY_ORDER,
  emptyHseDigestSettings,
  normalizeHseDigestHour,
  normalizeHseDigestSettings,
  normalizeHseDigestWeekday,
  snapshotHseDigestSettings,
} from "./hseDigestSettingsUtils";

export function HseDigestSettingsForm() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(emptyHseDigestSettings);
  const [savedSnapshot, setSavedSnapshot] = useState(
    snapshotHseDigestSettings(emptyHseDigestSettings())
  );

  const applyLoaded = useCallback((data) => {
    const next = normalizeHseDigestSettings(data);
    setSettings(next);
    setSavedSnapshot(snapshotHseDigestSettings(next));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchHseDigestSettings();
      applyLoaded(data);
    } catch {
      toast.error(t("hse.digest.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [applyLoaded, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = useMemo(
    () => snapshotHseDigestSettings(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patchSettings = (patch) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateHseDigestSettings({
        enabled: settings.enabled === true,
        weekday: normalizeHseDigestWeekday(settings.weekday, 1),
        hour: normalizeHseDigestHour(settings.hour, 8),
        timezone: HSE_DIGEST_TIMEZONE,
      });
      applyLoaded(data);
      setSaveSuccess(true);
      toast.success(t("hse.digest.saved"));
      return true;
    } catch {
      setSaveError(true);
      toast.error(t("hse.digest.save_failed"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const digestHours = Array.from({ length: 24 }, (_, hour) => hour);

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <>
      <div className="mx-auto max-w-3xl space-y-4 pb-28">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              {t("hse.digest.enabled.title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.digest.enabled.description")}
            </p>
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
            <input
              type="checkbox"
              checked={settings.enabled === true}
              onChange={(event) =>
                patchSettings({ enabled: event.target.checked })
              }
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
            />
            <div className="text-sm font-medium text-slate-900">
              {t("hse.digest.enabled.toggle")}
            </div>
          </label>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-900">
              {t("hse.digest.schedule.title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.digest.schedule.description")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              {t("hse.digest.weekday")}
              <select
                value={settings.weekday}
                onChange={(event) =>
                  patchSettings({
                    weekday: parseInt(event.target.value, 10),
                  })
                }
                className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              >
                {HSE_DIGEST_WEEKDAY_ORDER.map((day) => (
                  <option key={day} value={day}>
                    {t(HSE_DIGEST_WEEKDAY_KEYS[day])}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              {t("hse.digest.hour")}
              <select
                value={settings.hour}
                onChange={(event) =>
                  patchSettings({
                    hour: parseInt(event.target.value, 10),
                  })
                }
                className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              >
                {digestHours.map((hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="mt-3 text-sm text-slate-500">
            {t("hse.digest.timezone")}: {t("hse.digest.timezone_value")}
          </p>
          <p className="mt-2 text-sm text-slate-500">
            {settings.last_digest_on
              ? t("hse.digest.last_sent", { date: settings.last_digest_on })
              : t("hse.digest.last_sent_never")}
          </p>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              {t("hse.digest.content.title")}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.digest.content.intro")}
            </p>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            <li>{t("hse.digest.content.events")}</li>
            <li>{t("hse.digest.content.documents")}</li>
            <li>{t("hse.digest.content.unread")}</li>
          </ul>
        </section>
      </div>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("hse.digest.saved")}
        errorMessage={t("hse.digest.save_failed")}
        onSave={handleSave}
        saveLabel={t("save")}
      />
    </>
  );
}
